import { useCallback, useEffect, useState } from 'react';
import type {
  FormAnswers, MicrositeServiceRow, MicrositeServiceForm,
  MicrositeServiceRequestRow, MicrositeServiceRequestDetail,
} from 'shared';
import { micrositeApi, ApiError } from '../lib/api';
import { FormFieldsInput } from '../components/FormFields';
import { Modal } from '../components/Modal';
import { Icon } from '../components/icons';
import { useMicrosite } from './MicrositeApp';

interface RegionOption {
  id: string; code: string; name: string; isProvince: boolean; contactPhone: string | null;
}

const REGION_KEY = 'pmi.microsite.region';

/**
 * The chosen wilayah, remembered per visitor.
 *
 * Wrapped because a browser in private mode, or one set to block site data, throws on
 * access rather than returning null. Losing the memory is a minor inconvenience; a
 * screen that will not render at all is not.
 */
const rememberedRegion = {
  get(): string | null {
    try { return localStorage.getItem(REGION_KEY); } catch { return null; }
  },
  set(id: string) {
    try { localStorage.setItem(REGION_KEY, id); } catch { /* private mode */ }
  },
};

const fmtWhen = (iso: string) => new Date(iso).toLocaleString('id-ID', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  timeZone: 'Asia/Jakarta',
});

/**
 * Layanan — the requester's side of the PMI service lines.
 *
 * Two things about this screen are deliberate and worth not undoing:
 *
 * 1. The dispatch number sits ABOVE the form for urgent services. An ambulance for a
 *    body at 2am is not a web-form job, and a form that queues behind an unwatched
 *    dashboard would be worse than no feature at all. The form is the non-urgent path.
 * 2. The questions are rendered from `form_field` through the SAME renderer the donor
 *    assessment uses. A field PMI adds in Form Asesmen appears here with no deploy, and
 *    there is no second renderer to drift from the first.
 */
export function MicrositeServices() {
  const { session } = useMicrosite();

  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [regionId, setRegionId] = useState<string | null>(rememberedRegion.get());
  const [catalogue, setCatalogue] = useState<MicrositeServiceRow[]>([]);
  const [mine, setMine] = useState<MicrositeServiceRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [requesting, setRequesting] = useState<MicrositeServiceRow | null>(null);
  const [form, setForm] = useState<MicrositeServiceForm | null>(null);
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [tracking, setTracking] = useState<MicrositeServiceRequestDetail | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await micrositeApi.get<RegionOption[]>('/services/regions');
        if (cancelled) return;
        setRegions(rows);
        // Only trust a remembered id that is still a live region: a wilayah PMI has
        // since deactivated would otherwise leave the screen stuck on an empty
        // catalogue with no obvious way out.
        setRegionId((current) => (
          current && rows.some((r) => r.id === current) ? current : null
        ));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Gagal memuat daftar wilayah.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadMine = useCallback(async () => {
    try {
      setMine(await micrositeApi.get<MicrositeServiceRequestRow[]>('/service-requests'));
    } catch {
      // History is secondary to making a request; a failure here must not blank the page.
    }
  }, []);

  useEffect(() => { void loadMine(); }, [loadMine]);

  useEffect(() => {
    if (!regionId) { setCatalogue([]); return; }
    let cancelled = false;
    setLoading(true);
    micrositeApi.get<MicrositeServiceRow[]>(`/services?regionId=${regionId}`)
      .then((rows) => { if (!cancelled) { setCatalogue(rows); setError(null); } })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Gagal memuat daftar layanan.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [regionId]);

  function chooseRegion(id: string) {
    rememberedRegion.set(id);
    setRegionId(id);
  }

  async function startRequest(row: MicrositeServiceRow) {
    if (!regionId) return;
    setRequesting(row);
    setForm(null);
    setAnswers({});
    setFormError(null);
    // Pre-filled from the AiYO profile, but EDITABLE: the person a dispatcher must reach
    // is often not the account holder. A son requests an ambulance from his own phone
    // for his mother's house, and the number to call may be the neighbour who is there.
    setContactName(session.profile.name);
    setContactPhone(session.profile.phone);
    try {
      setForm(await micrositeApi.get<MicrositeServiceForm>(
        `/services/${row.id}/form?regionId=${regionId}`,
      ));
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Gagal memuat formulir.');
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!requesting || !regionId) return;
    setBusy(true);
    setFormError(null);
    try {
      const created = await micrositeApi.post<{ number: string; statusLabel: string }>(
        `/services/${requesting.id}/request`,
        { regionId, contactName, contactPhone, answers },
      );
      setRequesting(null);
      setNotice(
        `Permintaan terkirim dengan nomor ${created.number}. `
        + 'Statusnya bisa Anda pantau di bawah.',
      );
      await loadMine();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Gagal mengirim permintaan.');
    } finally {
      setBusy(false);
    }
  }

  async function track(id: string) {
    try {
      setTracking(await micrositeApi.get<MicrositeServiceRequestDetail>(
        `/service-requests/${id}`,
      ));
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Gagal memuat status permintaan.');
    }
  }

  async function cancel(id: string) {
    setBusy(true);
    try {
      const next = await micrositeApi.post<MicrositeServiceRequestDetail>(
        `/service-requests/${id}/cancel`, { reason: null },
      );
      setTracking(next);
      setNotice('Permintaan dibatalkan.');
      await loadMine();
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Gagal membatalkan permintaan.');
    } finally {
      setBusy(false);
    }
  }

  const region = regions.find((r) => r.id === regionId) ?? null;
  const hasUrgent = catalogue.some((c) => c.isUrgent);
  const urgentPhone = catalogue.find((c) => c.isUrgent && c.contactPhone)?.contactPhone
    ?? region?.contactPhone
    ?? null;

  return (
    <div className="stack" style={{ gap: '1rem' }}>
      {notice && <div className="msg ok">{notice}</div>}
      {error && <div className="msg error">{error}</div>}

      {!regionId ? (
        <section className="stack" style={{ gap: '0.7rem' }}>
          <h3>Pilih wilayah Anda</h3>
          <p className="muted" style={{ fontSize: 13 }}>
            Layanan dan nomor yang bisa dihubungi berbeda tiap wilayah, jadi kami perlu
            tahu Anda di mana.
          </p>
          {loading && <p className="muted">Memuat…</p>}
          <div className="stack" style={{ gap: '0.5rem' }}>
            {regions.map((r) => (
              <button
                key={r.id}
                type="button"
                className="ms-svc"
                onClick={() => chooseRegion(r.id)}
              >
                <span className="ms-svc-ico"><Icon name="branch" size={16} /></span>
                <span className="ms-svc-body">
                  <strong>{r.name}</strong>
                  {r.isProvince && (
                    <span className="muted">Tingkat provinsi, untuk kejadian lintas kota</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : (
        <>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="label">Wilayah: {region?.name ?? '—'}</span>
            <button type="button" className="ghost" onClick={() => setRegionId(null)}>
              Ubah wilayah
            </button>
          </div>

          {/*
            The urgent callout is either a real number or an explicit warning. It must
            never be silently absent: a requester who needs an ambulance now would then
            see only a form, which is the exact outcome putting the phone first exists
            to prevent.
          */}
          {hasUrgent && (urgentPhone ? (
            <a className="ms-callout" href={`tel:${urgentPhone}`}>
              <strong>Butuh ambulans sekarang?</strong>
              <span className="ms-callout-tel mono">{urgentPhone}</span>
              <span>Telepon lebih cepat daripada mengisi formulir.</span>
            </a>
          ) : (
            <div className="msg error">
              <strong>Keadaan darurat?</strong> Nomor telepon petugas untuk wilayah ini
              belum diisi PMI, jadi formulir di bawah adalah satu-satunya jalur di sini.
              Untuk keadaan darurat, hubungi PMI wilayah Anda langsung atau 112.
            </div>
          ))}

          <section className="stack" style={{ gap: '0.6rem' }}>
            <span className="label">Ajukan permintaan</span>
            {loading && <p className="muted">Memuat…</p>}
            {!loading && catalogue.length === 0 && (
              <p className="muted">
                Belum ada layanan yang dibuka di wilayah ini. Coba wilayah lain, atau
                hubungi PMI langsung.
              </p>
            )}
            {catalogue.map((row) => (
              <button
                key={row.id}
                type="button"
                className={row.isUrgent ? 'ms-svc is-urgent' : 'ms-svc'}
                onClick={() => startRequest(row)}
              >
                <span className="ms-svc-ico">
                  <Icon name={row.isUrgent ? 'heart' : 'clipboard'} size={16} />
                </span>
                <span className="ms-svc-body">
                  <strong>{row.name}</strong>
                  {row.description && <span className="muted">{row.description}</span>}
                </span>
              </button>
            ))}
          </section>
        </>
      )}

      <section className="stack" style={{ gap: '0.6rem' }}>
        <span className="label">Riwayat layanan saya</span>
        {mine.length === 0 ? (
          <p className="muted" style={{ fontSize: 13 }}>
            Belum ada permintaan layanan.
          </p>
        ) : mine.map((row) => (
          <button key={row.id} type="button" className="ms-svc" onClick={() => track(row.id)}>
            <span className="ms-svc-body">
              <strong>{row.serviceTypeName}</strong>
              <span className="muted">
                <span className="mono">{row.number}</span> · {fmtWhen(row.createdAt)}
              </span>
            </span>
            <span className="pill">{row.statusLabel}</span>
          </button>
        ))}
      </section>

      {/* ── request form ── */}
      <Modal
        open={requesting !== null}
        title={requesting?.name ?? ''}
        description={region ? `${region.name}` : undefined}
        onClose={() => setRequesting(null)}
        onSubmit={submit}
        footer={
          <>
            <button type="button" className="ghost" onClick={() => setRequesting(null)}>
              Batal
            </button>
            <button type="submit" className="primary" disabled={busy || !form}>
              {busy ? 'Mengirim…' : 'Kirim permintaan'}
            </button>
          </>
        }
      >
        <div className="stack" style={{ gap: '1rem' }}>
          {formError && <div className="msg error">{formError}</div>}

          {requesting?.isUrgent && (requesting.contactPhone ? (
            <div className="msg">
              Kalau ini keadaan darurat, telepon{' '}
              <a href={`tel:${requesting.contactPhone}`} className="mono">
                {requesting.contactPhone}
              </a>{' '}
              — jauh lebih cepat daripada menunggu formulir ini diproses.
            </div>
          ) : (
            <div className="msg error">
              Formulir ini diproses petugas pada jam kerja. Kalau keadaannya darurat,
              hubungi PMI wilayah Anda langsung atau 112 — jangan menunggu formulir ini.
            </div>
          ))}

          <label className="field">
            <span className="label">Nama yang bisa dihubungi *</span>
            <input
              value={contactName} required
              onChange={(e) => setContactName(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="label">Nomor yang bisa dihubungi *</span>
            <input
              value={contactPhone} required inputMode="tel"
              onChange={(e) => setContactPhone(e.target.value)}
            />
            <span className="muted" style={{ fontSize: 11.5 }}>
              Boleh nomor orang lain kalau dia yang ada di lokasi.
            </span>
          </label>

          {form ? (
            <FormFieldsInput
              fields={form.fields}
              answers={answers}
              onChange={(key, value) => setAnswers((a) => ({ ...a, [key]: value }))}
            />
          ) : !formError && <p className="muted">Memuat pertanyaan…</p>}
        </div>
      </Modal>

      {/* ── tracking ── */}
      <Modal
        open={tracking !== null}
        title={tracking?.serviceTypeName ?? ''}
        description={tracking ? `${tracking.number} · ${tracking.regionName}` : undefined}
        onClose={() => setTracking(null)}
        footer={
          <>
            {tracking?.canCancel && (
              <button
                type="button" className="ghost" disabled={busy}
                onClick={() => void cancel(tracking.id)}
              >
                Batalkan permintaan
              </button>
            )}
            <button type="button" className="primary" onClick={() => setTracking(null)}>
              Tutup
            </button>
          </>
        }
      >
        {tracking && (
          <div className="stack" style={{ gap: '1rem' }}>
            <ol className="timeline">
              {tracking.steps.map((s) => (
                <li
                  key={s.status}
                  className={
                    s.reachedAt ? 'tl-step is-done'
                      : s.status === tracking.status ? 'tl-step is-now'
                        : 'tl-step'
                  }
                >
                  <span className="tl-node" aria-hidden="true" />
                  <div>
                    <strong>{s.label}</strong>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {s.reachedAt ? fmtWhen(s.reachedAt) : 'Menunggu'}
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            {tracking.contactPhone && (
              <a className="ms-svc" href={`tel:${tracking.contactPhone}`}>
                <span className="ms-svc-ico"><Icon name="idCard" size={16} /></span>
                <span className="ms-svc-body">
                  <strong>Hubungi petugas</strong>
                  <span className="muted mono">{tracking.contactPhone}</span>
                </span>
              </a>
            )}

            {tracking.answers.length > 0 && (
              <div className="field">
                <span className="label">Yang Anda kirim</span>
                <dl className="answers">
                  {tracking.answers.map((a) => (
                    <div key={a.key}>
                      <dt>{a.label}</dt>
                      <dd>
                        {a.value === null || a.value === '' ? '—'
                          : typeof a.value === 'boolean' ? (a.value ? 'Ya' : 'Tidak')
                            : Array.isArray(a.value) ? a.value.join(', ')
                              : String(a.value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
